import { z } from 'zod'

import { errorCodeSchema, type ErrorCode } from './errors'

/**
 * Collection metadata. Present only on list responses; `nextCursor` is null on
 * the last page rather than absent, so a client can loop on `!== null` alone.
 */
export const apiMetaSchema = z.object({
  nextCursor: z.string().nullish(),
  total: z.number().int().nonnegative().optional(),
})

export type ApiMeta = z.infer<typeof apiMetaSchema>

/**
 * Success envelope for a given payload schema. Built as a function because the
 * payload differs per endpoint while the wrapper never does — the API validates
 * outbound with the same schema the clients type against.
 */
export const apiSuccess = <T extends z.ZodType>(schema: T) =>
  z.object({
    data: schema,
    meta: apiMetaSchema.optional(),
  })

export type ApiSuccess<T> = {
  data: T
  meta?: ApiMeta
}

export const apiErrorBodySchema = z.object({
  code: errorCodeSchema,
  message: z.string(),
  /** Per-code payload — `fieldErrors`, `retryAfterSeconds`, `limit`/`current`. */
  details: z.record(z.string(), z.unknown()).optional(),
  /**
   * Echoed on success too, and in the `X-Request-Id` header and the server log
   * line. A support ticket quoting one id resolves to exactly one request.
   */
  requestId: z.string().min(1),
})

export const apiErrorSchema = z.object({
  error: apiErrorBodySchema,
})

export type ApiErrorBody = {
  code: ErrorCode
  message: string
  details?: Record<string, unknown>
  requestId: string
}

export type ApiError = { error: ApiErrorBody }

export type ApiResponse<T> = ApiSuccess<T> | ApiError

/** Narrows a parsed envelope without re-reading the HTTP status. */
export const isApiError = <T>(response: ApiResponse<T>): response is ApiError =>
  'error' in response
